import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Plus, Minus, Users } from 'lucide-react';
import { format, isWeekend, getWeek } from 'date-fns';
import EmployeeRow from './EmployeeRow';
import SpacerRow from './SpacerRow';
import { formatDateDDMMYYYY } from '../utils/helpers';

const EmployeeGrid = ({
  planEmployees,
  employees,
  dates,
  copiedData,
  defaultWorkHours,
  defaultTravelHours,
  isHoliday,
  getEmployeeUnavailability,
  getEntry,
  getAvailabilityTypeInfo,
  onMoveEmployee,
  onReorderEmployees,
  onEditEmployee,
  onRemoveEmployee,
  onAddSpacer,
  onRemoveSpacer,
  onSetDayToDefault,
  onSetDayToZero,
  onUpdateEntry,
  onCopyDay,
  onPasteDay,
  onCopyWeek,
  onPasteWeek
}) => {
  const { t } = useTranslation();
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Group dates by week for headers
  const weekGroups = [];
  let currentWeek = null;
  dates.forEach((date, idx) => {
    const weekNum = getWeek(date, { weekStartsOn: 1 });
    if (!currentWeek || currentWeek.weekNum !== weekNum) {
      currentWeek = { weekNum, startIdx: idx, days: [] };
      weekGroups.push(currentWeek);
    }
    currentWeek.days.push(date);
  });

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragOverIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = draggedIndex;
    
    if (sourceIndex !== null && sourceIndex !== targetIndex && onReorderEmployees) {
      onReorderEmployees(sourceIndex, targetIndex);
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  if (planEmployees.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('auto.ctr.teamPlanning')}
          </CardTitle>
          <CardDescription>{t('auto.ctr.noEmployeesAddedToThisPlan')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Click &quot;Add Employee&quot; above to start building your team</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('auto.ctr.teamPlanning')}
            </CardTitle>
            <CardDescription>
              {planEmployees.filter(e => !e.isSpacer).length} employees • Drag to reorder
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onAddSpacer} data-testid="btn-addspacer">
            <Plus className="h-4 w-4 mr-1" />
            <Minus className="h-4 w-4 mr-1" />
            {t('auto.ctr.addSpacer')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Mobile scrollable container */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="min-w-max">
            <Table>
              <TableHeader>
                {/* Week number row */}
                <TableRow>
                  <TableHead className="sticky left-0 bg-gray-50 z-20 min-w-[180px]">
                    {t('auto.ctr.employee')}
                  </TableHead>
                  {weekGroups.map((week) => (
                    <TableHead 
                      key={week.weekNum}
                      colSpan={week.days.length}
                      className="text-center bg-blue-50 border-l border-blue-200"
                    >
                      Week {week.weekNum}
                    </TableHead>
                  ))}
                </TableRow>
                
                {/* Day headers row */}
                <TableRow>
                  <TableHead className="sticky left-0 bg-gray-50 z-20">
                    <div className="flex justify-between text-xs">
                      <span>{t('auto.ctr.workHours')}</span>
                      <span className="text-blue-600">{t('auto.ctr.travelHours')}</span>
                    </div>
                  </TableHead>
                  {dates.map((date) => {
                    const { plHoliday, projHoliday, customHoliday } = isHoliday(date);
                    const weekend = isWeekend(date);
                    const isNonWorking = plHoliday || projHoliday || customHoliday;
                    
                    return (
                      <TableHead 
                        key={date.toISOString()}
                        className={`text-center min-w-[80px] ${weekend ? 'bg-orange-50' : ''} ${isNonWorking ? 'bg-red-50' : ''}`}
                      >
                        <div className="text-xs text-gray-500">{format(date, 'EEE')}</div>
                        <div className={`text-sm ${isNonWorking ? 'text-red-600' : ''}`}>
                          {formatDateDDMMYYYY(date)}
                        </div>
                        {plHoliday && (
                          <div className="text-xs text-red-600 truncate" title={plHoliday.name}>
                            🇵🇱 {plHoliday.name}
                          </div>
                        )}
                        {projHoliday && (
                          <div className="text-xs text-red-600 truncate" title={projHoliday.name}>
                            🏴 {projHoliday.name}
                          </div>
                        )}
                        {customHoliday && (
                          <div className="text-xs text-red-600">{t('auto.ctr.custom')}</div>
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {planEmployees.map((item, index) => (
                  item.isSpacer ? (
                    <SpacerRow
                      key={item.id}
                      spacer={item}
                      dates={dates}
                      index={index}
                      isDragging={draggedIndex === index}
                      isDragOver={dragOverIndex === index}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                      onMoveUp={() => onMoveEmployee(index, 'up')}
                      onMoveDown={() => onMoveEmployee(index, 'down')}
                      onRemove={() => onRemoveSpacer(item.id)}
                    />
                  ) : (
                    <EmployeeRow
                      key={item.id}
                      employee={item}
                      employees={employees}
                      dates={dates}
                      index={index}
                      isDragging={draggedIndex === index}
                      isDragOver={dragOverIndex === index}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                      copiedData={copiedData}
                      defaultWorkHours={defaultWorkHours}
                      defaultTravelHours={defaultTravelHours}
                      isHoliday={isHoliday}
                      getEmployeeUnavailability={getEmployeeUnavailability}
                      getEntry={getEntry}
                      getAvailabilityTypeInfo={getAvailabilityTypeInfo}
                      onMoveUp={() => onMoveEmployee(index, 'up')}
                      onMoveDown={() => onMoveEmployee(index, 'down')}
                      onEdit={() => onEditEmployee(item)}
                      onRemove={() => onRemoveEmployee(item.id)}
                      onSetDayToDefault={onSetDayToDefault}
                      onSetDayToZero={onSetDayToZero}
                      onUpdateEntry={onUpdateEntry}
                      onCopyDay={onCopyDay}
                      onPasteDay={onPasteDay}
                      onCopyWeek={onCopyWeek}
                      onPasteWeek={onPasteWeek}
                    />
                  )
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmployeeGrid;
